// apps/embed/src/chat-widget/chatWidgetLanguage.ts
//
// What the host's `data-lang` attribute means for the website chat widget. It sets two different
// languages (docs/content-translation.md §2.15.7):
//   * the CONVERSATION's language, sent with every visitor message: the bot replies in it and a
//     booking made in the chat is confirmed in it. When the host names no language, none is sent,
//     and the server answers in the business's default language (`fetchCustomerLocale`), never in
//     English by accident;
//   * the widget's own CHROME (its UI strings, the phone field's country names), which needs some
//     language to load at boot: the host's, else English. The strings endpoint takes the language
//     in its path and returns no business default, so without a `data-lang` the chrome stays
//     English while the conversation follows the business.
// A `data-lang` naming a language TourTask does not support is the page's setup mistake, read as every
// widget reads it (`readWidgetLanguage`): the panel names the code and the supported ones
// (`WidgetLanguageAlert`) and loads nothing, rather than reporting the strings endpoint's refusal as a
// connection error.
import type { AppLocale } from '@tourtask/types/i18n';
import { readWidgetLanguage, type WidgetLanguageProblem } from '@tourtask/storefront/utils/widgetLanguage';

/** The chrome language when the host names none. */
export const CHAT_CHROME_FALLBACK_LANG: AppLocale = 'en';

export type ChatWidgetLanguages =
  | {
      kind: 'ready';
      /** The language the widget's own UI strings and country list load in. */
      chromeLang: AppLocale;
      /** The language sent with the conversation; `null` when the host named none. */
      visitorLocale: AppLocale | null;
    }
  | Extract<WidgetLanguageProblem, { kind: 'unsupported' }>;

/** Reads the host's `data-lang` (`null` when the attribute is absent). */
export function readChatWidgetLanguages(dataLang: string | null): ChatWidgetLanguages {
  const language = readWidgetLanguage(dataLang);
  switch (language.kind) {
    case 'absent':
      return { kind: 'ready', chromeLang: CHAT_CHROME_FALLBACK_LANG, visitorLocale: null };
    case 'supported':
      return { kind: 'ready', chromeLang: language.locale, visitorLocale: language.locale };
    case 'unsupported':
      return language;
  }
}
